import { ScrollView, StyleSheet, View } from "react-native";
import { useCallback, useMemo, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { CheckSquareIcon } from "@phosphor-icons/react";

import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import {
  RecentTodosDocument,
  Todo,
  TodosDocument,
  useTodosQuery,
  useUpdateTodoMutation,
} from "../graphql/generated/graphql";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import { TodoCardList } from "../components/chatNewInterface/chatLayout/todoCardList/TodoCardList";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import Sentry from "../utils/sentry";
import { colors, semanticColors } from "../styles/design-system";
import { useAppState, actions } from "../contexts/AppStateContext";
import TaskFormModalContainer from "../containers/tasks/TaskFormModalContainer/TaskFormModalContainer";
import CompanionLayout from "../layouts/CompanionLayout";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { useCompanionThreads } from "../contexts/CompanionThreadsContext";

const TodoView = () => {
  const { t } = useTranslation();
  const { showError } = useCompanionThreads();
  const { data, refetch } = useTodosQuery({});
  const todosResult = data?.getTodosForUser;
  const todosSuccess = todosResult?.success !== false;

  const { primaryColor } = useAppConfig();

  const [activeTab, setActiveTab] = useState("tasks");

  const tasks = todosResult?.data ?? [];

  const incompleteTasks = todosResult?.data?.filter((todo) => !todo.completed) ?? [];
  const completedTodos = todosResult?.data?.filter((todo) => todo.completed) ?? [];

  const { dispatch } = useAppState();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Todo | undefined>(undefined);

  const handleModalOpen = () => {
    setEditingTask(undefined);
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setEditingTask(undefined);
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setModalVisible(false);
  };

  const handleEditTask = useCallback(
    (todo: Todo) => {
      setEditingTask(todo);
      dispatch({
        type: actions.SET_META,
        payload: { keypressIsListening: false },
      });
      setModalVisible(true);
    },
    [dispatch]
  );

  const tabs = useMemo(
    () => [
      {
        key: "tasks",
        title: `${t("Active")} (${incompleteTasks.length})`,
      },
      {
        key: "completed",
        title: `${t("Completed")} (${completedTodos.length})`,
      },
    ],
    [t, incompleteTasks, completedTodos]
  );

  const [updateTodoMutation] = useUpdateTodoMutation({
    refetchQueries: [{ query: TodosDocument }, { query: RecentTodosDocument }],
  });

  const displayedTodos = activeTab === "tasks" ? incompleteTasks : completedTodos;

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const handleCheckboxToggle = useCallback(
    async (taskId: string) => {
      try {
        const task = tasks?.find((task) => task.id === taskId);
        if (task == null) {
          return;
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const newValue = !task.completed;

        const workspaceId = task.workspacesHasTodo?.[0]?.id || null;

        const variables = {
          id: taskId,
          todo: {
            name: task.name,
            timezone,
            completed_date: newValue ? new Date().toISOString() : undefined,
            completed: newValue,
            description: task.description || undefined,
            priority: task.priority || undefined,
            date: task.date ? new Date(task.date).toISOString() : undefined,
            isRecurring: task.isRecurring || undefined,
            recurringFrequency: task.recurringFrequency || undefined,
            recurringDaily: task.recurringDaily || undefined,
            recurringDayOfWeek: task.recurringDayOfWeek || undefined,
            recurringDayOfMonth: task.recurringDayOfMonth || undefined,
            recurringTime: task.recurringTime || undefined,
            timeOfDay: task.timeOfDay || undefined,
            workspaceId: workspaceId || undefined,
          },
        };

        await updateTodoMutation({ variables });
      } catch (error) {
        Sentry.captureException(error);
        const message = error instanceof Error ? error.message : "Failed to update the to-do. Please try again.";
        showError({
          title: "Failed to update to-do",
          message,
          onRetry: async () => {
            await handleCheckboxToggle(taskId);
          },
          retryButtonText: "Retry",
        });
      }
    },
    [tasks, updateTodoMutation, showError]
  );

  return (
    <CompanionLayout>
      <View style={styles.container}>
        <WorkspaceHeader title={t("To-Dos")} />
        <View style={styles.tabWrapper}>
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
            activeTabColor={primaryColor}
            useTranslationText={false}
          />
          <CustomButton
            text={"To-do"}
            styleType="primaryLightened"
            onPress={handleModalOpen}
            leftIcon={<Plus color="#fff" size={16} />}
            aria-label="Add a Task"
            accessibilityLabel="Add a Task"
            size="s"
            style={styles.addTaskBtn}
            textStyle={styles.addTaskBtnText}
          />
        </View>
        <ScrollView style={styles.scrollView} tabIndex={0}>
          <View style={styles.todoSectionContainer}>
            {todosSuccess === false ? (
              <View style={styles.errorState}>
                <CustomText
                  text={todosResult?.message ?? "We were not able to get your to-dos."}
                  size="m"
                  useTranslationText={false}
                  style={styles.errorMessage}
                />
                <CustomButton
                  text="Retry"
                  onPress={() => void refetch()}
                  useTranslationText={false}
                  style={styles.retryButton}
                />
              </View>
            ) : displayedTodos.length === 0 && activeTab === "tasks" ? (
              <View style={styles.emptyState}>
                <View style={styles.emptyStateIcon}>
                  <CheckSquareIcon size={32} color="rgb(152 162 179)" alt="No active to-dos" role="img" />
                </View>
                <CustomSpacing type="vertical" size="s" />
                <CustomSpacing type="vertical" size="l" />
                <CustomText text="No active to-dos" size="xl" weight="bold" style={styles.emptyStateTitle} />
                <CustomSpacing type="vertical" size="s" />
                <CustomText
                  text="To-dos created from your chat conversations will appear here, or create one manually."
                  size="s"
                  style={styles.emptyStateSubtitle}
                />
                <CustomSpacing type="vertical" size="xxl" />
                <CustomButton
                  text="Create to-do"
                  styleType="primaryLightened"
                  onPress={handleModalOpen}
                  leftIcon={<Plus color="#fff" size={16} />}
                  aria-label="Create a Task"
                  size="s"
                  style={[styles.addTaskBtn, styles.emptyStateButton]}
                  textStyle={styles.addTaskBtnText}
                />
              </View>
            ) : (
              <TodoCardList
                todos={displayedTodos as Todo[]}
                toggleCompletion={handleCheckboxToggle}
                onEdit={handleEditTask}
              />
            )}
          </View>
        </ScrollView>
        <TaskFormModalContainer visible={modalVisible} onClose={handleModalClose} task={editingTask} />
      </View>
    </CompanionLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    display: "flex",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#f9fafb",
  },
  tabWrapper: {
    marginVertical: 24,
    width: "100%",
    maxWidth: 900,
    marginHorizontal: "auto",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  todoSectionContainer: {
    width: "100%",
    maxWidth: 900,
    marginHorizontal: "auto",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 48,
  },
  emptyStateIcon: {
    backgroundColor: "rgb(242 244 247)",
    borderRadius: 16,
    padding: 16,
  },
  emptyStateButton: {
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  emptyStateTitle: {
    color: "#101828",
  },
  emptyStateSubtitle: {
    color: "#475467",
    textAlign: "center",
    maxWidth: 384,
  },
  addTaskBtn: {
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: colors.brand[500],
  },
  addTaskBtnText: {
    color: semanticColors.typography.textWhite,
  },
  errorState: {
    alignItems: "center",
    paddingVertical: 48,
    gap: 12,
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: 8,
  },
  retryButton: {
    minWidth: 120,
  },
});

export default TodoView;
